import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Search, Filter, PlusCircle, ArrowUpDown, Edit, Trash2 } from "lucide-react";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import accessManagementService from "../../services/accessManagementService";
import { useQuery } from "@tanstack/react-query";
import EditMemberDialog from "./EditMemberDialog.js";
import { DeleteUserMutation } from "../../mutations/AccessManagementMutationHook";
import { useToast } from "../../context/ToastContext";
import { ProgressSpinner } from "primereact/progressspinner";
import { Skeleton } from "primereact/skeleton";

// MUI Dialog Imports
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import MuiButton from '@mui/material/Button'; // Aliased to avoid conflict if PrimeReact Button is used elsewhere

// MUI Table Imports
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import Box from '@mui/material/Box';


const UserManagement = ({ setEnableNewMemberPopUp }) => {

  const {
    data: allUserData = [],
    isLoading: isAllUserDataLoading,
    isFetching: isAllUserDataFetching,
  } = useQuery({
    queryKey: ["getAllUserList"],
    queryFn: () => accessManagementService.getAllUser(),
  });

  const toast = useToast();

  const { mutate: deleteUser, isPending: isDeleting } = DeleteUserMutation(
    () => {
      toast({ severity: "info", summary: "Confirmed", detail: "User Deleted Successfully.", life: 3000 });
      handleCloseDeleteDialog(); // Close MUI dialog on success
    },
    (error) => {
      toast({ severity: "error", summary: "Error", detail: error.response.data.detail || error.message, life: 3000 });
      handleCloseDeleteDialog(); // Close MUI dialog on error
    }
  );

  // State and Handlers for MUI Delete Dialog
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);

  const handleOpenDeleteDialog = (user) => {
    setUserToDelete(user);
    setOpenDeleteDialog(true);
  };

  const handleCloseDeleteDialog = () => {
    setOpenDeleteDialog(false);
    setUserToDelete(null);
  };

  const handleConfirmDelete = () => {
    if (userToDelete) {
      deleteUser([userToDelete.user_id]);
      // Dialog will be closed by the deleteUser mutation's callbacks (onSuccess/onError)
    }
  };

  const [searchQuery, setSearchQuery] = useState("");
  const [showFilters, setShowFilters] = useState(false);
  const [selectedView, setSelectedView] = useState("Members");
  const [currentPage, setCurrentPage] = useState(1);
  const [enableEditMemberPopUp, setEnableEditMemberPopUp] = useState(false);
  const [editUserData, setEditUserData] = useState(null);
  const [filterDomain, setFilterDomain] = useState("");
  const [filterSubDomain, setFilterSubDomain] = useState("");
  const [filterSubSubDomain, setFilterSubSubDomain] = useState("");
  const [filterProduct, setFilterProduct] = useState("");
  const [filterRoleName, setFilterRoleName] = useState("");

  const rowsPerPage = 10;

  const landingPage = {
    userList:
      allUserData
    ,
    allDataAssets: [],
  };

  const unfilteredData =
    selectedView === "Members" ? landingPage.userList : landingPage.allDataAssets;

  const dataSource = unfilteredData.filter((item) => {
    const domainMatch = filterDomain ? item.domains?.some(d => d.parent_domain === filterDomain) : true;
    const subDomainMatch = filterSubDomain ? item.domains?.some(d => d.domain === filterSubDomain) : true;
    const subSubDomainMatch = filterSubSubDomain ? item.domains?.some(d => d.subdomains.includes(filterSubSubDomain)) : true;
    const productMatch = filterProduct ? item.product_names?.some(p => p === filterProduct) : true;
    const roleMatch = filterRoleName ? item.role_name === filterRoleName : true;
    const searchMatch = searchQuery
      ? item.user_email.toLowerCase().includes(searchQuery.toLowerCase())
      : true;

    return (
      domainMatch &&
      subDomainMatch &&
      subSubDomainMatch &&
      productMatch &&
      roleMatch &&
      searchMatch
    );
  });

  const indexOfLastRow = currentPage * rowsPerPage;
  const indexOfFirstRow = indexOfLastRow - rowsPerPage;
  const currentRows = dataSource.slice(indexOfFirstRow, indexOfLastRow);
  const totalPages = Math.ceil(dataSource.length / rowsPerPage);

  const parentDomainList = allUserData.filter(item => item.domains?.length > 0)
                    .flatMap(item => item.domains.map(dom => dom.parent_domain))
                    .filter((value, index, self) => self.indexOf(value) === index);

  const domainList = allUserData.filter(item => item.domains?.length > 0)
                    .flatMap(item => item.domains.map(dom => dom.domain))
                    .filter((value, index, self) => self.indexOf(value) === index);

  const subDomainList = allUserData.filter(item => item.domains?.length > 0)
                    .flatMap(item => item.domains.flatMap(domain => domain.subdomains))
                    .filter((value, index, self) => self.indexOf(value) === index);
                    
  //const productList = allUserData? [...new Set(allUserData.map(item => item.product_names).filter(Boolean))] : [];
  const productList = allUserData.filter(item => item.product_names?.length > 0)
                    .flatMap(item => item.product_names)
                    .filter((value, index, self) => self.indexOf(value) === index);

  const roleNameList = allUserData ? [...new Set(allUserData.map(item => item.role_name).filter(Boolean))] : [];

  return (
    <div style={{ paddingLeft: "0px", paddingRight: "0px", width: "100%", maxWidth: "100%", overflow: "hidden" }} className="dashboard-container">
      <div className="dashboard-main p-4" style={{ width: "100%", maxWidth: "100%", overflow: "auto" }} >
        <div className="dashboard-header">
          {/* <div className="view-toggle">
            <button
              className={selectedView === "Members" ? "active" : ""}
              onClick={() => setSelectedView("Members")}
            >
              Members
            </button>
            <button
              className={selectedView === "all" ? "active" : ""}
              onClick={() => setSelectedView("all")}
            >
              All Data Assets
            </button>
          </div> */}

          <div className="dashboard-actions">
            <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
              <div className="search-box">
                <Search className="search-icon" />
                <input
                  type="text"
                  placeholder="Search for members"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>
              <button
                className="filter-button"
                onClick={() => setShowFilters(!showFilters)}
              >
                <Filter className="icon" />
                <span>Filter</span>
              </button>
            </div>

            <div onClick={() => setEnableNewMemberPopUp(true)} className="onboard-button">
              <PlusCircle className="icon" />
              <span>Add New Members</span>
            </div>
          </div>

          {showFilters && (
            <div className="filters-panel">
              <div className="filter-group">
                <label>Parent Domain</label>
                <select
                  value={filterDomain}
                  onChange={(e) => setFilterDomain(e.target.value)}
                >
                  <option value="">All</option>
                   {parentDomainList.map((domain, index) => (
                    <option key={index} value={domain}>{domain}</option>
                  ))}
                </select>
              </div>
              <div className="filter-group">
                <label>Domain</label>
                <select
                  value={filterSubDomain}
                  onChange={(e) => setFilterSubDomain(e.target.value)}
                >
                  <option value="">All</option>
                   {domainList.map((domain, index) => (
                    <option key={index} value={domain}>{domain}</option>
                  ))}
                </select>
              </div>
              <div className="filter-group">
                <label>Sub Domain</label>
                <select
                  value={filterSubSubDomain}
                  onChange={(e) => setFilterSubSubDomain(e.target.value)}
                >
                  <option value="">All</option>
                  {subDomainList.map((domain, index) => (
                    <option key={index} value={domain}>{domain}</option>
                  ))}
                </select>
              </div>
              <div className="filter-group">
                <label>Product</label>
                <select
                  value={filterProduct}
                  onChange={(e) => setFilterProduct(e.target.value)}
                >
                  <option value="">All</option>
                  {productList.map((product, index) => (
                    <option key={index} value={product}>{product}</option>
                  ))}
                </select>
              </div>
              <div className="filter-group">
                <label>Role Name</label>
                <select
                  value={filterRoleName}
                  onChange={(e) => setFilterRoleName(e.target.value)}
                >
                  <option value="">All</option>
                  {roleNameList.map((role, index) => (
                    <option key={index} value={role}>{role}</option>
                  ))}
                </select>
              </div>
            </div>
          )}

        </div>

        {isAllUserDataFetching ? (
          <div className="table-container mt-5">
            <div style={{ padding: "30px" }}>
              {/* Skeleton for table headers */}
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "10px" }}>
                {[...Array(8)].map((_, i) => (
                  <Skeleton key={i} width="12%" height="30px" style={{ marginRight: "10px" }} />
                ))}
              </div>
              {/* Skeletons for table rows */}
              {[...Array(5)].map((_, rowIndex) => (
                <div key={rowIndex} style={{ display: "flex", justifyContent: "space-between", marginBottom: "8px" }}>
                  {[...Array(8)].map((_, colIndex) => (
                    <Skeleton key={colIndex} width="12%" height="25px" style={{ marginRight: "10px" }} />
                  ))}
                </div>
              ))}
            </div>
          </div>
        ) : (
          <TableContainer
            component={Paper}
            sx={{
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
              // border: '1px solid #e0e0e0',
              borderRadius: '16px',
              maxHeight: '600px',
              overflow: 'auto',
              backgroundColor: '#F6F6F6',
              marginLeft: '10px',
              marginRight: '10px',
              width: 'calc(100% - 20px)', // Responsive width accounting for margins
              maxWidth: '100%'
            }}
          >
            <Table sx={{ minWidth: 650, width: '100%' }} stickyHeader>
              <TableHead>
                <TableRow>
                  {[
                    "User Email",
                    "Parent Domain",
                    "Domain",
                    "Sub Domain",
                    "Product",
                    "Role Name",
                    "Actions",
                  ].map((col, i) => (
                    <TableCell
                      key={i}
                      sx={{
                        color: "white",
                        fontWeight: 700,
                        fontSize: '14px',
                        padding: '20px 24px',
                        borderBottom: '3px solid #bb1ccc',
                        position: 'sticky',
                        top: 0,
                        zIndex: 2,
                        backgroundColor: '#F6F6F6 !important',
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px',
                        textAlign: col === 'Actions' ? 'center' : 'left'
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', justifyContent: col === 'Actions' ? 'center' : 'flex-start' }}>
                        <span style={{ color: '#bb1ccc' }}>{col}</span>
                        <ArrowUpDown className="icon" size={16} color="#bb1ccc" />
                      </div>
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {currentRows.map((table, index) => (
                  <TableRow
                    key={table.user_id}
                    sx={{
                      backgroundColor: index % 2 === 0 ? '#fafafa' : '#ffffff',
                      borderBottom: '1px solid #f0f0f0',
                      '&:hover': {
                        backgroundColor: '#f0f8ff',
                        transform: 'translateY(-1px)',
                        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                        transition: 'all 0.2s ease'
                      }
                    }}
                  >
                    <TableCell sx={{
                      padding: '20px 24px',
                      fontWeight: 600,
                      fontSize: '14px',
                      color: '#2c3e50',
                      borderBottom: '1px solid #f0f0f0',
                      maxWidth: '250px',
                      wordBreak: 'break-word'
                    }}>
                      {table.user_email}
                    </TableCell>
                    <TableCell sx={{
                      padding: '20px 24px',
                      fontSize: '14px',
                      color: '#555',
                      borderBottom: '1px solid #f0f0f0',
                      maxWidth: '200px',
                      wordBreak: 'break-word'
                    }}>
                      {table.domains?.map((d) => d.parent_domain).join(', ')}
                    </TableCell>
                    <TableCell sx={{
                      padding: '20px 24px',
                      fontSize: '14px',
                      color: '#555',
                      borderBottom: '1px solid #f0f0f0',
                      maxWidth: '200px',
                      wordBreak: 'break-word'
                    }}>
                      {table.domains?.map((d) => d.domain).join(', ')}
                    </TableCell>
                    <TableCell sx={{
                      padding: '20px 24px',
                      fontSize: '14px',
                      color: '#555',
                      borderBottom: '1px solid #f0f0f0',
                      maxWidth: '200px',
                      wordBreak: 'break-word'
                    }}>
                      {table.domains?.flatMap((d) => d.subdomains).join(', ')}
                    </TableCell>
                    <TableCell sx={{
                      padding: '20px 24px',
                      fontSize: '14px',
                      color: '#555',
                      borderBottom: '1px solid #f0f0f0',
                      maxWidth: '200px',
                      wordBreak: 'break-word'
                    }}>
                      {table.product_names?.join(', ')}
                    </TableCell>
                    <TableCell sx={{
                      padding: '20px 24px',
                      fontSize: '14px',
                      color: '#555',
                      borderBottom: '1px solid #f0f0f0',
                      maxWidth: '150px',
                      wordBreak: 'break-word'
                    }}>
                      {table.role_name}
                    </TableCell>
                    <TableCell sx={{
                      padding: '20px 24px',
                      fontSize: '14px',
                      borderBottom: '1px solid #f0f0f0',
                      minWidth: '100px',
                      textAlign: 'center'
                    }}>
                      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 1 }}>
                        <span className="bg-[#bb1ccc] px-2 py-2 rounded-[6px]">
                        <Edit
                          size={14}
                          color="#ffffff"
                          style={{
                            cursor: "pointer",
                            transition: 'all 0.2s ease',
                            '&:hover': {
                              color: '#000080',
                              transform: 'scale(1.1)'
                            }
                          }}
                          onClick={() => { setEditUserData(table); setEnableEditMemberPopUp(true); }}
                        />
                        </span>
                      
                        <span className="bg-red-500 px-2 py-2 rounded-[6px]">
                      <Trash2
                          size={14}
                          color="white"
                          style={{
                            cursor: "pointer",
                            transition: 'all 0.2s ease',
                            '&:hover': {
                              color: '#b71c1c',
                              transform: 'scale(1.1)'
                            }
                          }}
                          onClick={() => handleOpenDeleteDialog(table)}
                        />
                      </span>
                      
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        <div className="pagination" style={{ marginLeft: '10px', marginRight: '10px', maxWidth: 'calc(100% - 20px)' }}>
          <span>
            Showing {indexOfFirstRow + 1} to{" "}
            {Math.min(indexOfLastRow, dataSource.length)} of {dataSource.length}{" "}
            entries
          </span>
          <div className="pagination-controls">
            <button
              disabled={currentPage === 1}
              onClick={() => setCurrentPage((prev) => prev - 1)}
            >
              Previous
            </button>
            {[...Array(totalPages)].map((_, idx) => (
              <button
                key={idx}
                className={currentPage === idx + 1 ? "active" : ""}
                onClick={() => setCurrentPage(idx + 1)}
              >
                {idx + 1}
              </button>
            ))}
            <button
              disabled={currentPage === totalPages}
              onClick={() => setCurrentPage((prev) => prev + 1)}
            >
              Next
            </button>
          </div>
        </div>
      </div>
      {/* PrimeReact ConfirmDialog was here, now replaced by MUI Dialog below */}
      {/* <ConfirmDialog /> */}

      {/* MUI Delete Confirmation Dialog */}
      <Dialog
        open={openDeleteDialog}
        onClose={handleCloseDeleteDialog}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        PaperProps={{
          sx: {
            borderRadius: '12px',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
            minWidth: '400px'
          }
        }}
      >
        <DialogTitle 
          id="alert-dialog-title"
          sx={{
            fontWeight: 700,
            fontSize: '18px',
            color: '#0000a0',
            borderBottom: '1px solid #e0e0e0',
            padding: '20px 24px 16px 24px'
          }}
        >
          Confirm Deletion
        </DialogTitle>
        <DialogContent sx={{ padding: '20px 24px' }}>
          <DialogContentText 
            id="alert-dialog-description"
            sx={{
              fontSize: '14px',
              color: '#555',
              lineHeight: '1.5'
            }}
          >
            Are you sure you want to delete <strong>{userToDelete?.user_email}</strong>?
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ padding: '16px 24px 20px 24px', gap: 1 }}>
          <MuiButton 
            onClick={handleCloseDeleteDialog} 
            variant="outlined"
            sx={{
              borderColor: '#0000a0',
              color: '#0000a0',
              '&:hover': {
                borderColor: '#000080',
                backgroundColor: '#f0f8ff'
              }
            }}
          >
            Cancel
          </MuiButton>
          <MuiButton 
            onClick={handleConfirmDelete} 
            variant="contained"
            sx={{
              backgroundColor: '#d32f2f',
              '&:hover': {
                backgroundColor: '#b71c1c'
              }
            }}
            autoFocus
          >
            Delete
          </MuiButton>
        </DialogActions>
      </Dialog>

      {enableEditMemberPopUp && editUserData && (
        <EditMemberDialog onHide={() => setEnableEditMemberPopUp(false)} userData={editUserData} />
      )}
    </div>
  );
};

export default UserManagement;
